import { normalizeAlbumPath, toDeliveryPublicUrl, toThumbKey } from "./deliveryPath";
import { decodeToken } from "./deliveryToken";
import { getTranslator } from "../i18n";

const FOLDER_MIME = "application/vnd.folder";

export function getDeliveryApiBase() {
    const base = (process.env.REACT_APP_DELIVERY_API_URL || "").replace(/\/+$/, "");
    return base;
}

export function isDeliveryApiConfigured() {
    return Boolean(getDeliveryApiBase());
}

export function isFolderItem(mimeType) {
    return mimeType === FOLDER_MIME;
}

export function isImageItem(mimeType) {
    return typeof mimeType === "string" && mimeType.startsWith("image/");
}

export function isVideoItem(mimeType) {
    return typeof mimeType === "string" && mimeType.startsWith("video/");
}

function buildApiParams({ rootPrefix, extra = {} } = {}) {
    const params = new URLSearchParams();
    if (rootPrefix) {
        params.set("rootPrefix", rootPrefix);
    }
    Object.entries(extra).forEach(([key, value]) => {
        if (value != null && value !== "") {
            params.set(key, value);
        }
    });
    return params;
}

export async function listDeliveryFolder(prefix, { rootPrefix, pageToken } = {}) {
    const apiBase = getDeliveryApiBase();
    if (!apiBase) {
        const error = new Error(getTranslator()("delivery.api.missingApi"));
        error.code = "MISSING_API";
        throw error;
    }

    const params = buildApiParams({
        rootPrefix,
        extra: { prefix, pageToken },
    });

    let response;
    try {
        response = await fetch(`${apiBase}/folder?${params.toString()}`);
    } catch {
        const error = new Error("ALBUM_LOAD_FAILED");
        error.code = "NETWORK_ERROR";
        throw error;
    }

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = new Error(body.error?.message || "ALBUM_LOAD_FAILED");
        error.code = "API_ERROR";
        error.status = response.status;
        throw error;
    }

    return response.json();
}

export function getFileThumbnailUrl(file) {
    if (file.thumbnailUrl) {
        return file.thumbnailUrl;
    }
    if (isImageItem(file.mimeType)) {
        return toDeliveryPublicUrl(toThumbKey(file.id));
    }
    return null;
}

// 直连 R2 自定义域名，走 CDN 缓存，绝大多数情况下最快。
export function getFileDownloadUrl(file) {
    if (file.downloadUrl) {
        return file.downloadUrl;
    }
    return toDeliveryPublicUrl(file.id);
}

// Worker 代理下载地址：由 Worker 用 S3 凭证转发字节流，不走 R2 自定义域名。
// bucket CORS 配置叠加 CDN 边缘缓存，会导致 fetch() 直连偶发"同一 URL 时好时坏"的 CORS 报错
// （缓存命中时可能把另一个 Origin 的响应发给当前 Origin）。这里作为直连失败时的兜底，
// 牺牲一点速度换稳定（Cache-Control: no-store，不会被缓存污染）。
export function getFileProxyDownloadUrl(file) {
    return file.proxyDownloadUrl || null;
}

// 底层拼接函数：用于前端自己构造"文件对象里还没有 proxyDownloadUrl"的场景
// （如单文件交付链接、后台预览下载）。正常 /folder 列表返回的文件已自带该字段，无需再拼。
export function getProxyDownloadUrl(key, rootPrefix) {
    const apiBase = getDeliveryApiBase();
    if (!apiBase || !key || !rootPrefix) {
        return null;
    }
    const params = new URLSearchParams({ key, rootPrefix });
    return `${apiBase}/file?${params.toString()}`;
}

export function getImageViewUrl(key) {
    return toDeliveryPublicUrl(key);
}

// 凭短 ID 从 Worker（D1）公开读取交付详情。
// 命中返回交付详情；404 返回 null；网络/服务异常抛错（含 code/status）。
export async function fetchDeliveryInfo(id) {
    const apiBase = getDeliveryApiBase();
    if (!apiBase) {
        const error = new Error(getTranslator()("delivery.api.missingApi"));
        error.code = "MISSING_API";
        throw error;
    }

    // 破缓存：微信内置浏览器会缓存 GET 响应（含 404），加时间戳并禁用缓存。
    const url = `${apiBase}/api/delivery/info/${encodeURIComponent(id)}?_=${Date.now()}`;

    let response;
    try {
        response = await fetch(url, { cache: "no-store" });
    } catch {
        const error = new Error("DELIVERY_LOAD_FAILED");
        error.code = "NETWORK_ERROR";
        throw error;
    }

    if (response.status === 404) {
        return null;
    }

    // 410 Gone：服务端判定链接已过期，拒绝返回相册信息。
    if (response.status === 410) {
        const body = await response.json().catch(() => ({}));
        const error = new Error("DELIVERY_EXPIRED");
        error.code = "EXPIRED";
        error.status = 410;
        error.archiveMarkdown = body.error?.archiveMarkdown || body.archiveMarkdown || "";
        throw error;
    }

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = new Error(body.error?.message || "DELIVERY_LOAD_FAILED");
        error.code = "API_ERROR";
        error.status = response.status;
        throw error;
    }

    const body = await response.json().catch(() => ({}));
    return body.delivery || null;
}

// 统一解析交付链接 token：
// 1）老链接（自包含 base64 token）本地解码，零网络；
// 2）新短链（随机短 ID）回 D1 取详情。
// 返回交付详情对象 / null（无效或不存在）；网络异常抛错。
export async function resolveDeliveryPayload(token) {
    const legacy = decodeToken(token);
    if (legacy) {
        return legacy;
    }
    return fetchDeliveryInfo(token);
}

// “下载所有照片”的目标地址：
// - gdrive：谷歌云盘直链；
// - zip：R2 公开域名下的压缩包对象（无斜杠时默认拼到相册根路径下）。
export function getDownloadAllUrl(downloadAll, albumPath) {
    if (!downloadAll || !downloadAll.type || !downloadAll.value) {
        return null;
    }
    const value = String(downloadAll.value).trim();
    if (!value) {
        return null;
    }
    if (downloadAll.type === "gdrive") {
        return `https://drive.google.com/uc?export=download&id=${encodeURIComponent(value)}`;
    }
    if (downloadAll.type === "zip") {
        const root = normalizeAlbumPath(albumPath);
        const key = value.includes("/") || !root ? value : `${root}/${value}`;
        return toDeliveryPublicUrl(key);
    }
    return null;
}

export { FOLDER_MIME, toThumbKey };
